import {i18n, Oversettelser} from "./Oversettelser";
import {Injectable} from "@angular/core";
import {AgendaDokumentDel, Autentiseringsmetode, BenyttetSigneringsmetode, Deltakerstatus, InkludertGenerertDokument, OppgaveForelderType, Oppgavestatus, SigneringStatus, StemmegivningStatus, TilgangsrolleMedlem} from "../ressurser/generertNg2HttpKlient";
import {erNil} from "felles-ng2/støttefunksjoner/nilsjekk";
import {SkjermetEntitetType} from "felles-ng2/tjenester/datalager/SkjermingDatatjeneste";
import {caseForEnumMangler} from "felles-ng2/støttefunksjoner/enumer";

/** Brukerrettede representasjon av diverse sentrale enumer. */
@Injectable()
export class EnumOversettelser {

    constructor(private oversettelser: Oversettelser) {
    }

    oversettetBenyttetSigneringsmetode(signaturtype: BenyttetSigneringsmetode): string {
        switch (signaturtype) {
            case BenyttetSigneringsmetode.legacy_autentisert:
            case BenyttetSigneringsmetode.sp2_enkel:
                return this.oversettelser.oversett(i18n('Enkel signatur som innlogget bruker'));
            case BenyttetSigneringsmetode.lederplan_autentisert_tofakt_sms:
                return this.oversettelser.oversett(i18n('Engangskode per SMS'));
            case BenyttetSigneringsmetode.criipto_no_bankid_mobil_tekst:
                return this.oversettelser.oversett(i18n('Norsk BankID'));
            case BenyttetSigneringsmetode.criipto_no_bankid_central_tekst:
            case BenyttetSigneringsmetode.criipto_no_bankid_central_pdf:
            case BenyttetSigneringsmetode.criipto_no_bankid_pdf:
            case BenyttetSigneringsmetode.criipto_no_bankid_high_pdf:
            case BenyttetSigneringsmetode.legacy_no_bankid_central_pdf:
            case BenyttetSigneringsmetode.sp2_bankid:
            case BenyttetSigneringsmetode.bankid_qes:
                return this.oversettelser.oversett(i18n('Norsk BankID'));
            case BenyttetSigneringsmetode.criipto_se_bankid_samedevice:
            case BenyttetSigneringsmetode.criipto_se_bankid_anotherdevice:
            case BenyttetSigneringsmetode.criipto_se_bankid_anotherdevice_qr:
                return this.oversettelser.oversett(i18n('Svensk BankID'));
            default:
                console.warn('Ukjent BenyttetSigneringsmetode ' + signaturtype)
                return BenyttetSigneringsmetode[BenyttetSigneringsmetode.legacy_autentisert];
        }
    }

    oversettetAutentiseringsmetode(metode: Autentiseringsmetode): string {
        switch (metode) {
            case Autentiseringsmetode.passord: return this.oversettelser.oversett(i18n('Passord'));
            case Autentiseringsmetode.totpOtp: return this.oversettelser.oversett(i18n('Kodegenerator'));
            case Autentiseringsmetode.smsOtp: return this.oversettelser.oversett(i18n('Sms'));
            case Autentiseringsmetode.criiptoOpenId: return this.oversettelser.oversett(i18n('E-Id'));
            default: caseForEnumMangler(metode);
                throw new Error(`ukjent Autentiseringsmetode ${metode}`)
        }
    }

    oversettInkludertGenerertDokument(verdi: InkludertGenerertDokument): string {
        switch (verdi) {
            case InkludertGenerertDokument.ingen:
                return this.oversettelser.oversett(i18n('ingen'));
            case InkludertGenerertDokument.agenda:
                return this.oversettelser.oversett(i18n('Agenda for dette møte'));
            case InkludertGenerertDokument.protokoll:
                return this.oversettelser.oversett(i18n('Protokoll fra forrige møte'));
            case InkludertGenerertDokument.forfalteOppgaver:
                return this.oversettelser.oversett(i18n('Forfalte oppgaver'));
            default:
                //denne konstruksjonen sørger for kompileringsfeil dersom ikke alle enumverdier er inkludert i switch
                caseForEnumMangler(verdi);
                throw new Error(`ukjent InkludertGenerertDokument ${verdi}`)
        }
    }

    tilgangsrolleMedlem(verdi: TilgangsrolleMedlem, variant: 'tittel' | 'subtittel' = 'tittel'): string {
        const erTittel = variant === 'tittel';
        switch (verdi) {
            case TilgangsrolleMedlem.lese:
                return erTittel ?
                  this.oversettelser.oversett(i18n('Lesetilgang')) :
                  this.oversettelser.oversett(i18n('Bruker kan kun lese informasjon'));
            case TilgangsrolleMedlem.skrive:
                return erTittel ?
                  this.oversettelser.oversett(i18n('Administrator')) :
                  this.oversettelser.oversett(i18n('Bruker kan invitere medlemmer, opprette møter og saker etc.'));

            case TilgangsrolleMedlem.tilgangIkkePublisert:
                return erTittel ?
                  this.oversettelser.oversett(i18n('Tilgang til ikke-publisert informasjon')) :
                  this.oversettelser.oversett(i18n('Bruker kan se detaljer for ikke-publiserte møter og protokoller'));

            case TilgangsrolleMedlem.tilgangSlettetIHendelseslogg:
                return erTittel ?
                  this.oversettelser.oversett(i18n('Tilgang til hendelseslogg for slettede data')) :
                  this.oversettelser.oversett(i18n('Bruker kan se hendelseslogg-innslag for slettede møter, saker, dokumenter osv.'));

            default:
                //denne konstruksjonen sørger for kompileringsfeil dersom ikke alle enumverdier er inkludert i switch
                caseForEnumMangler(verdi);
                throw new Error(`ukjent TilgangsrolleMedlem ${verdi}`)
        }
    }

    oversettOppgaveStatus(status: Oppgavestatus): string {
        return this.oppgaveStatusOversettelser.get(status)!;
    }

    private _oppgaveStatusOversettelser!: Map<Oppgavestatus, string>;

    get oppgaveStatusOversettelser(): Map<Oppgavestatus, string> {
        if (erNil(this._oppgaveStatusOversettelser))
            this._oppgaveStatusOversettelser = new Map([
                [Oppgavestatus.ikke_påbegynt, this.oversettelser.oversett(i18n("Ikke påbegynt"))],
                [Oppgavestatus.påbegynt, this.oversettelser.oversett(i18n("Påbegynt"))],
                [Oppgavestatus.utført, this.oversettelser.oversett(i18n("Utført"))]
            ]);
        return this._oppgaveStatusOversettelser;
    }

    /** Returnerer tekst-representasjon av angitt SkjermetEntitetType */
    oversettSkjermetEntitetType(type: SkjermetEntitetType): { navn: string; forelder: string } {
        switch (type) {
            case SkjermetEntitetType.arkivmappe:
                return {navn: this.oversettelser.oversett(i18n('mappe')), forelder: this.oversettelser.oversett(i18n('forelder-mappe'))};
            case SkjermetEntitetType.arkivdokument:
                return {navn: this.oversettelser.oversett(i18n('arkivdokument')), forelder: this.oversettelser.oversett(i18n('mappe'))};
            case SkjermetEntitetType.saksdokument:
                return {navn: this.oversettelser.oversett(i18n('saksdokument')), forelder: this.oversettelser.oversett(i18n('sak'))};
            case SkjermetEntitetType.sak:
                return {navn: this.oversettelser.oversett(i18n('sak')), forelder: this.oversettelser.oversett(i18n('møte'))};
            case SkjermetEntitetType.moete:
                return {navn: this.oversettelser.oversett(i18n('møte')), forelder: ''}; //møte har ingen forelder i skjermings-sammenheng
            default:
                //denne konstruksjonen sørger for kompileringsfeil dersom ikke alle enumverdier er inkludert i switch
                caseForEnumMangler(type);
                throw new Error(`ukjent SkjermetEntitetType ${type}`)
        }
    }

    oversettDeltakerstatus(status: Deltakerstatus) {
        switch (status) {
            case Deltakerstatus.innkalling_ikke_sendt:
                return this.oversettelser.oversett(i18n('Ikke sendt'));
            case Deltakerstatus.innkalling_sendt:
                return this.oversettelser.oversett(i18n('Sendt'));
            case Deltakerstatus.akseptert:
                return this.oversettelser.oversett(i18n('Godtatt'));
            case Deltakerstatus.avslaatt:
                return this.oversettelser.oversett(i18n('Avslått'));
            default:
                //denne konstruksjonen sørger for kompileringsfeil dersom ikke alle enumverdier er inkludert i switch
                caseForEnumMangler(status);
        }
    }

    oversettStemmegivningStatus(status: StemmegivningStatus) {
        switch (status) {
            case StemmegivningStatus.ikkePåbegynt:
                return this.oversettelser.oversett(i18n('Ikke påbegynt'));
            case StemmegivningStatus.påbegynt:
                return this.oversettelser.oversett(i18n('Påbegynt'));
            case StemmegivningStatus.fullført:
                return this.oversettelser.oversett(i18n('Fullført'));
            default:
                //denne konstruksjonen sørger for kompileringsfeil dersom ikke alle enumverdier er inkludert i switch
                caseForEnumMangler(status);
        }
    }

    oversettSigneringStatus(status: SigneringStatus) {
        switch (status) {
            case SigneringStatus.ikkeAktivert:
                return this.oversettelser.oversett(i18n('Ikke signert'));
            case SigneringStatus.tilSignering:
                return this.oversettelser.oversett(i18n('Til signering'));
            case SigneringStatus.delvisSignert:
                return this.oversettelser.oversett(i18n('Delvis signert'));
            case SigneringStatus.signert:
                return this.oversettelser.oversett(i18n('Signert'));
            case SigneringStatus.eksterntSignert:
                return this.oversettelser.oversett(i18n('Eksternt signert'));
            case SigneringStatus.signertOgKonvoluttert:
                return this.oversettelser.oversett(i18n('Signering komplett'));
            default:
                //denne konstruksjonen sørger for kompileringsfeil dersom ikke alle enumverdier er inkludert i switch
                caseForEnumMangler(status);
        }
    }

    oversettAgendadokumentDel(del: AgendaDokumentDel): any {
        switch (del) {
            case AgendaDokumentDel.saksbeskrivelse:
                return this.oversettelser.oversett(i18n('Saksbeskrivelse'));
            case AgendaDokumentDel.beslutningsforslag:
                return this.oversettelser.oversett(i18n('Beslutningsforslag'));
            case AgendaDokumentDel.planlagte_deltakere:
                return this.oversettelser.oversett(i18n('Planlagte deltakere'));
            default:
                //denne konstruksjonen sørger for kompileringsfeil dersom ikke alle enumverdier er inkludert i switch
                caseForEnumMangler(del);
        }
    }

    oversettDokumentForelder(del: OppgaveForelderType): any {
        switch (del) {
            case OppgaveForelderType.gruppe:
                return this.oversettelser.oversett(i18n('Frittstående oppgaver i gruppen'));
            case OppgaveForelderType.sak:
                return this.oversettelser.oversett(i18n('Oppgaver knyttet til saker'));
            case OppgaveForelderType.beslutning:
                return this.oversettelser.oversett(i18n('Oppgaver knyttet til beslutninger'));
            default:
                //denne konstruksjonen sørger for kompileringsfeil dersom ikke alle enumverdier er inkludert i switch
                caseForEnumMangler(del);
        }
    }

    /* public oversettInvitasjonsstatus(status: l.Invitasjonsstatus) {
         switch (status) {
             case l.Invitasjonsstatus.ikke_sendt:
                 /// Invitasjonsstatus.ikke_sendt
                 return this.oversettelser.getString("Ikke sendt");
             case l.Invitasjonsstatus.sendt:
                 /// Invitasjonsstatus.sendt
                 return this.oversettelser.getString("Sendt");
             case l.Invitasjonsstatus.akseptert:
                 /// Invitasjonsstatus.akseptert
                 return this.oversettelser.getString("Godtatt");
             case l.Invitasjonsstatus.avslått:
                 /// Invitasjonsstatus.avslått
                 return this.oversettelser.getString("Avslått");
             default:
                 return status;
         }
     }

     public oversettMedlemsstatus(status: l.Medlemstatus) {
         switch (status) {
             case l.Medlemstatus.normal_tilgang:
                 /// Medlemstatus.normal_tilgang
                 return this.oversettelser.getString("Normal tilgang");
             case l.Medlemstatus.tilgang_stengt:
                 /// Medlemstatus.tilgang_stengt
                 return this.oversettelser.getString("Tilgang stengt");
             case l.Medlemstatus.anonymisert:
                 /// Medlemstatus.anonymisert
                 return this.oversettelser.getString("Anonymisert");
             default:
                 return status;
         }
     }*/

}
